/**
 * NWM (National Water Model) variable types, color ramps, and legends.
 *
 * Variables come from two NWM products:
 *   - Land file: SNOWH, QSNOW
 *   - Forcing file: T2D, U2D, V2D, Q2D, RAINRATE, SWDOWN
 */

import { LIGHTNING_LEVELS, ltngToUnified } from './lightning-scale.js'

export type NwmVariable =
  | 'snowh'
  | 't2d'
  | 'wind'
  | 'rainrate'
  | 'qsnow'
  | 'q2d'
  | 'swdown'
  | 'ltng'
  | 'tcdc'
  | 'smoke'
  | 'refc'
  | 'vis'
  | 'preciptype'
  | 'precip'
  | 'goes_glm'
  | 'goes_cloud'
  | 'mrms_radar'
  | 'cape'
  | 'lightning_unified'

export interface NwmColorStop {
  value: number // value in display units
  color: [number, number, number, number] // RGBA 0-255
}

export interface GradientLegendConfig {
  stops: { position: number; color: string }[] // position 0-1, CSS color
  ticks: { position: number; label: string }[] // position 0-1, label text
}

export interface ClassificationLegendEntry {
  color: string
  label: string
}

export interface NwmVariableConfig {
  id: NwmVariable
  label: string
  description: string
  unit: string
  /** NetCDF variable name(s) — wind uses ['U2D', 'V2D'] */
  ncVariables: string[]
  /** Which product file: 'land', 'forcing' (NWM), 'hrrr' (HRRR-only), 'goes' (GOES satellite), or 'mrms' (NEXRAD radar) */
  source: 'land' | 'forcing' | 'hrrr' | 'goes' | 'mrms'
  nodata: number
  ramp?: NwmColorStop[]
  legend?: GradientLegendConfig
  /** Categorical legend for bitmask/classification layers */
  classificationLegend?: ClassificationLegendEntry[]
  /** Descriptive unit label shown below gradient legend (e.g., "Peak predicted flashes/km²") */
  unitDescription?: string
  /** Convert raw NetCDF units to display units */
  transform?: (raw: number) => number
  /** True for wind — requires two COGs (U2D + V2D) */
  isVector?: boolean
  /** Secondary COG variable to load alongside the primary (e.g., preciptype alongside rainrate) */
  secondaryCogVariable?: string
  /** Source of the secondary COG if different from primary (e.g., 'mrms' for MRMS radar alongside GOES cloud) */
  secondaryCogSource?: 'land' | 'forcing' | 'hrrr' | 'goes' | 'mrms'
}

// ---- Temperature (T2D): Kelvin → °F ----

const TEMP_RAMP: NwmColorStop[] = [
  { value: -40, color: [69, 10, 130, 220] }, // deep purple (extreme cold)
  { value: -20, color: [49, 54, 149, 220] }, // indigo
  { value: 0, color: [69, 117, 180, 220] }, // blue
  { value: 15, color: [116, 173, 209, 220] }, // light blue
  { value: 32, color: [255, 255, 255, 200] }, // white (freezing)
  { value: 50, color: [254, 224, 144, 220] }, // light yellow
  { value: 70, color: [253, 174, 97, 220] }, // orange
  { value: 90, color: [215, 48, 39, 220] }, // red
  { value: 110, color: [165, 0, 38, 220] }, // dark red
]

// ---- Wind speed (U2D + V2D → mph) ----

const WIND_RAMP: NwmColorStop[] = [
  { value: 0, color: [200, 230, 200, 0] }, // transparent (calm)
  { value: 3, color: [144, 202, 149, 180] }, // green
  { value: 8, color: [255, 235, 59, 200] }, // yellow
  { value: 15, color: [255, 152, 0, 210] }, // orange
  { value: 25, color: [244, 67, 54, 220] }, // red
  { value: 40, color: [156, 39, 176, 230] }, // purple
  { value: 60, color: [103, 58, 183, 240] }, // deep purple
]

// ---- Precipitation rate (RAINRATE: mm/s → in/hr) ----

const PRECIP_RAMP: NwmColorStop[] = [
  { value: 0, color: [200, 230, 200, 0] }, // transparent
  { value: 0.01, color: [200, 230, 200, 150] }, // very light green
  { value: 0.1, color: [76, 175, 80, 200] }, // green
  { value: 0.25, color: [33, 150, 243, 210] }, // blue
  { value: 0.5, color: [26, 35, 126, 220] }, // dark blue
  { value: 1.0, color: [156, 39, 176, 230] }, // purple
  { value: 2.0, color: [233, 30, 99, 240] }, // magenta
]

// ---- Snowfall rate (QSNOW: mm/s → in/hr) ----

const SNOWFALL_RAMP: NwmColorStop[] = [
  { value: 0, color: [220, 230, 255, 0] }, // transparent
  { value: 0.1, color: [187, 222, 251, 170] }, // very light blue
  { value: 0.25, color: [144, 202, 249, 190] }, // light blue
  { value: 0.5, color: [100, 181, 246, 210] }, // blue
  { value: 1.0, color: [255, 255, 255, 220] }, // white
  { value: 2.0, color: [206, 147, 216, 230] }, // light purple
  { value: 4.0, color: [156, 39, 176, 240] }, // purple
]

// ---- Humidity (Q2D: kg/kg → g/kg) ----

const HUMIDITY_RAMP: NwmColorStop[] = [
  { value: 0, color: [240, 248, 255, 0] }, // transparent
  { value: 2, color: [178, 223, 219, 160] }, // light teal
  { value: 5, color: [128, 203, 196, 190] }, // teal
  { value: 8, color: [77, 182, 172, 210] }, // medium teal
  { value: 12, color: [38, 166, 154, 220] }, // dark teal
  { value: 16, color: [0, 137, 123, 230] }, // deep teal
  { value: 20, color: [0, 105, 92, 240] }, // very dark teal
]

// ---- Solar radiation (SWDOWN: W/m²) ----

const SOLAR_RAMP: NwmColorStop[] = [
  { value: 0, color: [30, 30, 60, 0] }, // transparent (dark)
  { value: 50, color: [255, 248, 225, 140] }, // pale yellow
  { value: 200, color: [255, 235, 59, 180] }, // yellow
  { value: 400, color: [255, 193, 7, 210] }, // amber
  { value: 600, color: [255, 152, 0, 220] }, // orange
  { value: 800, color: [255, 87, 34, 230] }, // deep orange
  { value: 1000, color: [213, 0, 0, 240] }, // red
]

// ---- Lightning (LTNG: flashes/km²/5min) ----

const LIGHTNING_RAMP: NwmColorStop[] = [
  { value: 0, color: [255, 255, 200, 0] },
  { value: 0.01, color: [255, 255, 150, 150] },
  { value: 0.05, color: [255, 235, 59, 190] },
  { value: 0.1, color: [255, 193, 7, 210] },
  { value: 0.2, color: [255, 152, 0, 220] },
  { value: 0.4, color: [244, 67, 54, 230] },
  { value: 0.7, color: [156, 39, 176, 240] },
  { value: 1.0, color: [106, 27, 154, 250] },
]

// ---- Unified lightning index (0..1) — shared with the OM lightning renderer via
// lightning-scale.ts. The HRRR LTNG field is mapped to the 0..1 index by the
// config's `transform` (ltngToUnified), so the ramp + legend are keyed on the
// index, not raw flashes. Both ramp and legend are DERIVED from LIGHTNING_LEVELS
// so the scale has a single source of truth. ----

const UNIFIED_LIGHTNING_RAMP: NwmColorStop[] = LIGHTNING_LEVELS.map(l => ({
  value: l.index,
  color: [l.rgb[0], l.rgb[1], l.rgb[2], l.alpha],
}))

const UNIFIED_LIGHTNING_LEGEND: GradientLegendConfig = {
  stops: LIGHTNING_LEVELS.map(l => ({
    position: l.index,
    color: `rgba(${l.rgb[0]},${l.rgb[1]},${l.rgb[2]},${(l.alpha / 255).toFixed(2)})`,
  })),
  ticks: LIGHTNING_LEVELS.filter(l => l.index > 0).map(l => ({
    position: l.index,
    label: l.label,
  })),
}

// ---- Smoke density (MASSDEN: μg/m³) ----

const SMOKE_RAMP: NwmColorStop[] = [
  { value: 0, color: [255, 255, 220, 0] },
  { value: 5, color: [255, 245, 180, 100] },
  { value: 20, color: [255, 220, 120, 160] },
  { value: 50, color: [255, 180, 60, 190] },
  { value: 100, color: [200, 120, 40, 210] },
  { value: 250, color: [150, 70, 30, 230] },
  { value: 500, color: [100, 40, 20, 245] },
]

// ---- Composite reflectivity (REFC: dBZ) ----

const REFLECTIVITY_RAMP: NwmColorStop[] = [
  { value: 0, color: [0, 0, 0, 0] },
  { value: 5, color: [4, 233, 231, 150] },
  { value: 10, color: [1, 159, 244, 170] },
  { value: 20, color: [3, 0, 244, 190] },
  { value: 30, color: [2, 253, 2, 200] },
  { value: 40, color: [1, 197, 1, 210] },
  { value: 45, color: [254, 255, 2, 220] },
  { value: 50, color: [229, 188, 0, 225] },
  { value: 55, color: [253, 0, 2, 230] },
  { value: 60, color: [212, 0, 0, 235] },
  { value: 65, color: [248, 0, 253, 240] },
  { value: 75, color: [152, 84, 198, 250] },
]

// ---- Visibility (VIS: meters → miles, inverted ramp) ----

const VISIBILITY_RAMP: NwmColorStop[] = [
  { value: 0, color: [139, 0, 0, 220] },
  { value: 0.25, color: [220, 20, 60, 200] },
  { value: 0.5, color: [255, 69, 0, 180] },
  { value: 1, color: [255, 140, 0, 150] },
  { value: 2, color: [255, 215, 0, 100] },
  { value: 5, color: [255, 255, 150, 50] },
  { value: 10, color: [255, 255, 200, 0] },
]

// ---- CAPE (Convective Available Potential Energy: J/kg) ----

const CAPE_RAMP: NwmColorStop[] = [
  { value: 0, color: [0, 128, 0, 0] },
  { value: 250, color: [40, 160, 0, 140] },
  { value: 500, color: [96, 192, 0, 170] },
  { value: 1000, color: [255, 255, 0, 200] },
  { value: 1500, color: [255, 210, 0, 210] },
  { value: 2000, color: [255, 165, 0, 220] },
  { value: 3000, color: [255, 83, 0, 230] },
  { value: 4000, color: [255, 0, 0, 240] },
  { value: 5000, color: [200, 0, 200, 250] },
]

// ---- GOES GLM Lightning density (fl/km²) ----

const GOES_GLM_RAMP: NwmColorStop[] = [
  { value: 0, color: [255, 255, 200, 0] },
  { value: 0.01, color: [255, 255, 150, 150] },
  { value: 0.05, color: [255, 235, 59, 190] },
  { value: 0.1, color: [255, 193, 7, 210] },
  { value: 0.2, color: [255, 152, 0, 220] },
  { value: 0.4, color: [244, 67, 54, 230] },
  { value: 0.7, color: [156, 39, 176, 240] },
  { value: 1.0, color: [106, 27, 154, 250] },
]

// ---- Cloud + Cloud Water composite (kg/m² → light-to-blue color, alpha from cloud %) ----
// Higher cloud water content = thicker/more impactful cloud = bluer

const CLOUD_WATER_RAMP: NwmColorStop[] = [
  { value: 0, color: [225, 228, 235, 255] },
  { value: 0.005, color: [215, 220, 232, 255] },
  { value: 0.02, color: [200, 208, 225, 255] },
  { value: 0.06, color: [180, 190, 215, 255] },
  { value: 0.15, color: [155, 168, 200, 255] },
  { value: 0.4, color: [120, 138, 185, 255] },
  { value: 1.0, color: [80, 100, 165, 255] },
  { value: 3.0, color: [25, 40, 120, 255] },
]

// ---- NWM Snow depth (SNOWH: meters → inches) ----

const NWM_SNOW_RAMP: NwmColorStop[] = [
  { value: 0, color: [224, 240, 255, 0] }, // transparent
  { value: 0.04, color: [224, 240, 255, 180] }, // very light blue (trace)
  { value: 1, color: [144, 202, 249, 200] }, // light blue
  { value: 6, color: [66, 165, 245, 210] }, // blue
  { value: 12, color: [30, 136, 229, 220] }, // medium blue
  { value: 24, color: [21, 101, 192, 230] }, // dark blue
  { value: 48, color: [13, 71, 161, 240] }, // navy
  { value: 72, color: [106, 27, 154, 245] }, // dark purple
  { value: 120, color: [74, 20, 140, 250] }, // deep purple
]

// ---- Variable configurations ----

export const NWM_CONFIGS: Record<NwmVariable, NwmVariableConfig> = {
  snowh: {
    id: 'snowh',
    label: 'Modeled (NWM)',
    description: 'NWM modeled 1km hourly',
    unit: 'in',
    ncVariables: ['SNOWH'],
    source: 'land',
    nodata: -9999,
    ramp: NWM_SNOW_RAMP,
    // meters → inches
    transform: (raw: number) => raw * 39.3701,
    legend: {
      stops: [
        { position: 0, color: '#E0F0FF' },
        { position: 0.08, color: '#90CAF9' },
        { position: 0.2, color: '#42A5F5' },
        { position: 0.4, color: '#1E88E5' },
        { position: 0.6, color: '#1565C0' },
        { position: 0.75, color: '#0D47A1' },
        { position: 0.88, color: '#6A1B9A' },
        { position: 1, color: '#4A148C' },
      ],
      ticks: [
        { position: 0, label: '0"' },
        { position: 0.4, label: "2'" },
        { position: 1, label: "10'+" },
      ],
    },
  },

  t2d: {
    id: 't2d',
    label: 'Temperature',
    description: 'NWM 1km hourly',
    unit: '°F',
    ncVariables: ['T2D'],
    source: 'forcing',
    nodata: -9999,
    ramp: TEMP_RAMP,
    // Kelvin → °F
    transform: (raw: number) => ((raw - 273.15) * 9) / 5 + 32,
    legend: {
      stops: [
        { position: 0, color: '#450a82' },
        { position: 0.15, color: '#313695' },
        { position: 0.27, color: '#4575b4' },
        { position: 0.37, color: '#74add1' },
        { position: 0.48, color: '#ffffff' },
        { position: 0.6, color: '#fee090' },
        { position: 0.73, color: '#fdae61' },
        { position: 0.87, color: '#d73027' },
        { position: 1, color: '#a50026' },
      ],
      ticks: [
        { position: 0, label: '-40°' },
        { position: 0.48, label: '32°' },
        { position: 1, label: '110°' },
      ],
    },
  },

  wind: {
    id: 'wind',
    label: 'Wind',
    description: 'NWM 1km hourly',
    unit: 'mph',
    ncVariables: ['U2D', 'V2D'],
    source: 'forcing',
    nodata: -9999,
    ramp: WIND_RAMP,
    // m/s → mph
    transform: (raw: number) => raw * 2.23694,
    isVector: true,
    legend: {
      stops: [
        { position: 0, color: '#90ca95' },
        { position: 0.13, color: '#ffeb3b' },
        { position: 0.25, color: '#ff9800' },
        { position: 0.42, color: '#f44336' },
        { position: 0.67, color: '#9c27b0' },
        { position: 1, color: '#673ab7' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.42, label: '25' },
        { position: 1, label: '60+ mph' },
      ],
    },
  },

  rainrate: {
    id: 'rainrate',
    label: 'Precipitation',
    description: 'NWM 1km hourly',
    unit: 'in/hr',
    ncVariables: ['RAINRATE'],
    source: 'forcing',
    nodata: -9999,
    ramp: PRECIP_RAMP,
    // mm/s → in/hr: × 3600 / 25.4
    transform: (raw: number) => (raw * 3600) / 25.4,
    legend: {
      stops: [
        { position: 0, color: '#c8e6c8' },
        { position: 0.15, color: '#4caf50' },
        { position: 0.3, color: '#2196f3' },
        { position: 0.5, color: '#1a237e' },
        { position: 0.75, color: '#9c27b0' },
        { position: 1, color: '#e91e63' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.5, label: '0.5' },
        { position: 1, label: '2+ in/hr' },
      ],
    },
  },

  qsnow: {
    id: 'qsnow',
    label: 'Snowfall Rate',
    description: 'NWM 1km hourly',
    unit: 'in/hr',
    ncVariables: ['QSNOW'],
    source: 'land',
    nodata: -9999,
    ramp: SNOWFALL_RAMP,
    // mm/s → in/hr: × 3600 / 25.4
    transform: (raw: number) => (raw * 3600) / 25.4,
    legend: {
      stops: [
        { position: 0, color: '#bbdefb' },
        { position: 0.17, color: '#90caf9' },
        { position: 0.33, color: '#64b5f6' },
        { position: 0.5, color: '#ffffff' },
        { position: 0.75, color: '#ce93d8' },
        { position: 1, color: '#9c27b0' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.5, label: '1' },
        { position: 1, label: '4+ in/hr' },
      ],
    },
  },

  q2d: {
    id: 'q2d',
    label: 'Humidity (US Only)',
    description: 'NWM 1km hourly',
    unit: 'g/kg',
    ncVariables: ['Q2D'],
    source: 'forcing',
    nodata: -9999,
    ramp: HUMIDITY_RAMP,
    // kg/kg → g/kg
    transform: (raw: number) => raw * 1000,
    legend: {
      stops: [
        { position: 0, color: '#b2dfdb' },
        { position: 0.25, color: '#80cbc4' },
        { position: 0.5, color: '#4db6ac' },
        { position: 0.75, color: '#26a69a' },
        { position: 1, color: '#00695c' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.5, label: '10' },
        { position: 1, label: '20+ g/kg' },
      ],
    },
  },

  swdown: {
    id: 'swdown',
    label: 'Solar Radiation (US Only)',
    description: 'NWM 1km hourly',
    unit: 'W/m²',
    ncVariables: ['SWDOWN'],
    source: 'forcing',
    nodata: -9999,
    ramp: SOLAR_RAMP,
    legend: {
      stops: [
        { position: 0, color: '#1e1e3c' },
        { position: 0.1, color: '#fff8e1' },
        { position: 0.3, color: '#ffeb3b' },
        { position: 0.5, color: '#ffc107' },
        { position: 0.7, color: '#ff9800' },
        { position: 0.85, color: '#ff5722' },
        { position: 1, color: '#d50000' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.5, label: '500' },
        { position: 1, label: '1000 W/m²' },
      ],
    },
  },

  ltng: {
    id: 'ltng',
    label: 'Lightning (Forecast)',
    description: 'HRRR 3km peak flash density forecast',
    unit: 'fl/km²',
    unitDescription: 'Peak predicted flashes/km²',
    ncVariables: ['LTNG'],
    source: 'hrrr',
    nodata: -9999,
    ramp: LIGHTNING_RAMP,
    legend: {
      stops: [
        { position: 0, color: 'rgba(255,255,200,0)' },
        { position: 0.07, color: '#ffeb3b' },
        { position: 0.15, color: '#ffc107' },
        { position: 0.3, color: '#ff9800' },
        { position: 0.5, color: '#f44336' },
        { position: 0.75, color: '#9c27b0' },
        { position: 1, color: '#6a1b9a' },
      ],
      ticks: [
        { position: 0.12, label: 'Low\n(0.04)' },
        { position: 0.4, label: 'Moderate\n(0.1)' },
        { position: 0.75, label: 'High\n(0.3)' },
      ],
    },
  },

  // Unified lightning over CONUS: same HRRR LTNG COG as `ltng`, but normalised to
  // the worldwide 0..1 index so it renders identically to the OM lightning tiers.
  // Driven by the lightning coordinator service (not in ALL_NWM_SERVICES).
  lightning_unified: {
    id: 'lightning_unified',
    label: 'Lightning',
    description: 'HRRR 3km flash density, normalised to the unified lightning index',
    unit: '',
    unitDescription: 'Thunderstorm activity & severity',
    ncVariables: ['LTNG'],
    source: 'hrrr',
    nodata: -9999,
    transform: ltngToUnified,
    ramp: UNIFIED_LIGHTNING_RAMP,
    legend: UNIFIED_LIGHTNING_LEGEND,
  },

  tcdc: {
    id: 'tcdc',
    label: 'Cloud Cover (Forecast)',
    description: 'HRRR 3km hourly forecast',
    unit: '%',
    ncVariables: ['TCDC'],
    source: 'hrrr',
    nodata: -9999,
    ramp: CLOUD_WATER_RAMP,
    secondaryCogVariable: 'cloudwater',
    legend: {
      stops: [
        { position: 0, color: 'rgba(225,228,235,0)' },
        { position: 0.15, color: 'rgba(225,228,235,0.6)' },
        { position: 0.4, color: 'rgba(175,185,210,0.75)' },
        { position: 0.7, color: 'rgba(100,115,175,0.85)' },
        { position: 1, color: 'rgba(25,40,120,0.95)' },
      ],
      ticks: [
        { position: 0, label: 'Clear' },
        { position: 0.3, label: 'Thin' },
        { position: 1, label: 'Thick' },
      ],
    },
  },

  smoke: {
    id: 'smoke',
    label: 'Smoke',
    description: 'HRRR-Smoke 3km hourly forecast',
    unit: 'μg/m³',
    ncVariables: ['SMOKE'],
    source: 'hrrr',
    nodata: -9999,
    ramp: SMOKE_RAMP,
    legend: {
      stops: [
        { position: 0, color: 'rgba(255,255,220,0)' },
        { position: 0.1, color: '#fff4b4' },
        { position: 0.2, color: '#ffdc78' },
        { position: 0.4, color: '#ffb43c' },
        { position: 0.6, color: '#c87828' },
        { position: 0.8, color: '#96461e' },
        { position: 1, color: '#642814' },
      ],
      ticks: [
        { position: 0, label: '0' },
        { position: 0.4, label: '50' },
        { position: 1, label: '500+ μg/m³' },
      ],
    },
  },

  refc: {
    id: 'refc',
    label: 'Radar (US Only)',
    description: 'HRRR 3km hourly forecast',
    unit: 'dBZ',
    ncVariables: ['REFC'],
    source: 'hrrr',
    nodata: -9999,
    ramp: REFLECTIVITY_RAMP,
    legend: {
      stops: [
        { position: 0, color: '#04e9e7' },
        { position: 0.13, color: '#019ff4' },
        { position: 0.27, color: '#0300f4' },
        { position: 0.4, color: '#02fd02' },
        { position: 0.53, color: '#01c501' },
        { position: 0.6, color: '#feff02' },
        { position: 0.67, color: '#e5bc00' },
        { position: 0.73, color: '#fd0002' },
        { position: 0.8, color: '#d40000' },
        { position: 0.87, color: '#f800fd' },
        { position: 1, color: '#9854c6' },
      ],
      ticks: [
        { position: 0, label: '5' },
        { position: 0.4, label: '30' },
        { position: 1, label: '75 dBZ' },
      ],
    },
  },

  vis: {
    id: 'vis',
    label: 'Visibility',
    description: 'HRRR 3km hourly forecast',
    unit: 'mi',
    ncVariables: ['VIS'],
    source: 'hrrr',
    nodata: -9999,
    ramp: VISIBILITY_RAMP,
    transform: (raw: number) => raw * 0.000621371,
    legend: {
      stops: [
        { position: 0, color: '#8b0000' },
        { position: 0.1, color: '#dc143c' },
        { position: 0.2, color: '#ff4500' },
        { position: 0.35, color: '#ff8c00' },
        { position: 0.5, color: '#ffd700' },
        { position: 0.75, color: '#ffff96' },
        { position: 1, color: 'rgba(255,255,200,0)' },
      ],
      ticks: [
        { position: 0, label: '0 mi' },
        { position: 0.35, label: '1' },
        { position: 1, label: '10+' },
      ],
    },
  },

  cape: {
    id: 'cape',
    label: 'Thunderstorm Potential',
    description: 'HRRR 3km CAPE forecast',
    unit: 'J/kg',
    unitDescription: 'Convective Available Potential Energy (J/kg)',
    ncVariables: ['CAPE'],
    source: 'hrrr',
    nodata: -9999,
    ramp: CAPE_RAMP,
    legend: {
      stops: [
        { position: 0, color: 'rgba(0,128,0,0)' },
        { position: 0.05, color: '#28a000' },
        { position: 0.1, color: '#60c000' },
        { position: 0.2, color: '#ffff00' },
        { position: 0.3, color: '#ffd200' },
        { position: 0.4, color: '#ffa500' },
        { position: 0.6, color: '#ff5300' },
        { position: 0.8, color: '#ff0000' },
        { position: 1, color: '#c800c8' },
      ],
      ticks: [
        { position: 0.12, label: 'Low\n(250)' },
        { position: 0.35, label: 'Moderate\n(1000)' },
        { position: 0.65, label: 'Strong\n(2500)' },
        { position: 0.9, label: 'Extreme\n(4000+)' },
      ],
    },
  },

  preciptype: {
    id: 'preciptype',
    label: 'Precipitation Type',
    description: 'HRRR 3km hourly forecast',
    unit: '',
    ncVariables: ['PRECIPTYPE'],
    source: 'hrrr',
    nodata: -9999,
    classificationLegend: [
      { color: '#2196f3', label: 'Snow' },
      { color: '#4caf50', label: 'Rain' },
      { color: '#ff9800', label: 'Freezing Rain' },
      { color: '#9c27b0', label: 'Ice Pellets' },
    ],
  },

  precip: {
    id: 'precip',
    label: 'Precipitation',
    description: 'NWM 1km rate + HRRR 3km type',
    unit: 'in/hr',
    ncVariables: ['RAINRATE'],
    source: 'forcing',
    nodata: -9999,
    transform: (raw: number) => (raw * 3600) / 25.4,
    secondaryCogVariable: 'preciptype',
    classificationLegend: [
      { color: '#4caf50', label: 'Rain' },
      { color: '#42a5f5', label: 'Snow' },
      { color: '#e91e63', label: 'Freezing Rain' },
      { color: '#9c27b0', label: 'Ice Pellets' },
    ],
  },

  goes_glm: {
    id: 'goes_glm',
    label: 'Lightning (GLM)',
    description: 'GOES GLM flash density, 15-min',
    unit: 'fl/km²',
    unitDescription: 'Flashes/km²',
    ncVariables: ['GLM'],
    source: 'goes',
    nodata: 0,
    ramp: GOES_GLM_RAMP,
    legend: {
      stops: [
        { position: 0, color: 'rgba(255,255,200,0)' },
        { position: 0.07, color: '#ffeb3b' },
        { position: 0.15, color: '#ffc107' },
        { position: 0.3, color: '#ff9800' },
        { position: 0.5, color: '#f44336' },
        { position: 0.75, color: '#9c27b0' },
        { position: 1, color: '#6a1b9a' },
      ],
      ticks: [
        { position: 0.12, label: 'Low\n(0.04)' },
        { position: 0.4, label: 'Moderate\n(0.1)' },
        { position: 0.75, label: 'High\n(0.3)' },
      ],
    },
  },

  goes_cloud: {
    id: 'goes_cloud',
    label: 'Cloud Cover (GOES)',
    description: 'GOES ABI cloud probability, 15-min',
    unit: '%',
    ncVariables: ['CLOUD'],
    source: 'goes',
    nodata: 0,
    ramp: CLOUD_WATER_RAMP,
    transform: (raw: number) => raw * 100,
    secondaryCogVariable: 'cloudwater',
    secondaryCogSource: 'hrrr',
    legend: {
      stops: [
        { position: 0, color: 'rgba(225,228,235,0)' },
        { position: 0.15, color: 'rgba(225,228,235,0.6)' },
        { position: 0.4, color: 'rgba(175,185,210,0.75)' },
        { position: 0.7, color: 'rgba(100,115,175,0.85)' },
        { position: 1, color: 'rgba(25,40,120,0.95)' },
      ],
      ticks: [
        { position: 0, label: 'Clear' },
        { position: 0.3, label: 'Thin' },
        { position: 1, label: 'Thick' },
      ],
    },
  },

  mrms_radar: {
    id: 'mrms_radar',
    label: 'Radar',
    description: 'MRMS NEXRAD composite, 15-min',
    unit: 'dBZ',
    ncVariables: ['RADAR'],
    source: 'mrms',
    nodata: 0,
    ramp: REFLECTIVITY_RAMP,
    legend: {
      stops: [
        { position: 0, color: '#04e9e7' },
        { position: 0.13, color: '#019ff4' },
        { position: 0.27, color: '#0300f4' },
        { position: 0.4, color: '#02fd02' },
        { position: 0.53, color: '#01c501' },
        { position: 0.6, color: '#feff02' },
        { position: 0.67, color: '#e5bc00' },
        { position: 0.73, color: '#fd0002' },
        { position: 0.8, color: '#d40000' },
        { position: 0.87, color: '#f800fd' },
        { position: 1, color: '#9854c6' },
      ],
      ticks: [
        { position: 0, label: '5' },
        { position: 0.27, label: '20' },
        { position: 0.4, label: '30' },
        { position: 0.6, label: '45' },
        { position: 0.73, label: '55' },
        { position: 1, label: '75' },
      ],
    },
  },
}

/**
 * SNODAS gradient legend config (for retrofitting the existing snow depth overlay).
 * Same color scale as SNOW_DEPTH_RAMP in snow-depth-types.ts.
 */
export const SNODAS_GRADIENT_LEGEND: GradientLegendConfig = {
  stops: [
    { position: 0, color: '#E0F0FF' },
    { position: 0.08, color: '#90CAF9' },
    { position: 0.2, color: '#42A5F5' },
    { position: 0.4, color: '#1E88E5' },
    { position: 0.6, color: '#1565C0' },
    { position: 0.75, color: '#0D47A1' },
    { position: 0.88, color: '#6A1B9A' },
    { position: 1, color: '#4A148C' },
  ],
  ticks: [
    { position: 0, label: '0"' },
    { position: 0.1, label: '6"' },
    { position: 0.2, label: "1'" },
    { position: 0.4, label: "2'" },
    { position: 0.6, label: "4'" },
    { position: 0.75, label: "6'" },
    { position: 1, label: "10'+" },
  ],
}
